import { useLazyQuery } from '@apollo/client'
import { AppBar, Typography, useTheme } from '@material-ui/core'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import { ALLOCATION_LOG_DETAILS } from '../../graphql/inventoryAllocateAvailability'
import {
  addDaysToDate,
  DateObject,
  formatDateRange,
  getTodayDate
} from '../../helpers/date'
import { getTranslate, lang } from '../../localization'
import { AllocatedListInput } from '../../types/inventoryAllocatedList'
import { AllocationLogDetails } from '../../types/inventoryAllocationLogs'
import AllocateRoomtypeBox from './AttemptBox'
import InventoryDateRow from './InventoryDateRow'
import InventorySkeleton from './InventorySkeleton.skeleton'
import RequestInfoComponent from './RequestInfo'
import useStyles from './styles/index.styles'
import TotalAllocateRow from './totalAllocateRow'

function AllocationHistoryDetails() {
  const classes = useStyles()
  const theme = useTheme()

  const { requestID } = useParams<{ requestID: string }>()

  const [input, setinput] = useState<AllocatedListInput>({
    from: '',
    propertyCode: '',
    to: '',
    channelID: undefined,
    roomTypeCode: undefined
  })

  const [dateObject, setDateObject] = useState<DateObject>({
    gregorianStartDate: getTodayDate(),
    gregorianEndDate: addDaysToDate(getTodayDate(), 13)
  })

  const [
    getAllocationLogDetails,
    {
      data: allocationLogDetails,
      loading: loadingForAllocationDetails,
      refetch
    }
  ] = useLazyQuery<{
    allocationLogDetails: AllocationLogDetails
  }>(ALLOCATION_LOG_DETAILS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (resp) => {
      setDateObject({
        gregorianStartDate: resp.allocationLogDetails.allocationRange.from,
        gregorianEndDate: addDaysToDate(
          resp.allocationLogDetails.allocationRange.from,
          13
        )
      })
    }
  })

  useEffect(() => {
    setinput({
      ...input,
      from: dateObject.gregorianStartDate || '',
      to: dateObject.gregorianEndDate || ''
    })
  }, [dateObject])

  useEffect(() => {
    getAllocationLogDetails({
      variables: {
        requestId: requestID
      }
    })
  }, [])
  const cachedLevels = useMemo(
    () => [
      {
        label: getTranslate('Allocate History'),
        link: '/inventory/allocate-history'
      },
      {
        label: getTranslate('Attempt Details')
      }
    ],
    []
  )
  return (
    <>
      <div className={classes.root}>
        <AppBar
          className={classes.appBar}
          position="static"
          color="transparent">
          <Breadcrumbs levels={cachedLevels} loading={false} />
        </AppBar>
        <PageHeader title={getTranslate('Attempts')} />
        {allocationLogDetails && (
          <div className={classes.contentContainer}>
            <Typography variant="h3" gutterBottom>
              {getTranslate('Attempts Details')}
            </Typography>
            <RequestInfoComponent
              allocateNumber={allocationLogDetails.allocationLogDetails.delta}
              allocateRange={formatDateRange(
                allocationLogDetails.allocationLogDetails.allocationRange.from,
                allocationLogDetails.allocationLogDetails.allocationRange.to,
                lang
              )}
              channel={allocationLogDetails.allocationLogDetails.channelName}
              date={allocationLogDetails.allocationLogDetails.requestTime}
              email={allocationLogDetails.allocationLogDetails.userEmail}
              roomType={allocationLogDetails.allocationLogDetails.roomTypeName}
              status={allocationLogDetails.allocationLogDetails.status}
              type={allocationLogDetails.allocationLogDetails.action || ''}
              accommodation={
                allocationLogDetails.allocationLogDetails.accommodationName
              }
            />
            <InventoryDateRow
              refetch={refetch}
              selectedDateInDatePicker={dateObject}
              setselectedDateInDatePicker={setDateObject}
              allowedRange={{
                gregorianStartDate:
                  allocationLogDetails?.allocationLogDetails.allocationRange
                    .from,
                gregorianEndDate:
                  allocationLogDetails?.allocationLogDetails.allocationRange.to
              }}
            />
            {allocationLogDetails.allocationLogDetails.status == 'InProgress' &&
            allocationLogDetails.allocationLogDetails.attempts[0]
              .dailyAllocationDetails[0].requested == 0 ? (
              <InventorySkeleton refetch={refetch} />
            ) : (
              <div>
                {allocationLogDetails &&
                  allocationLogDetails.allocationLogDetails?.attempts.map(
                    (attempt) => (
                      <AllocateRoomtypeBox
                        attempt={attempt}
                        selectedDateInDatePicker={dateObject}
                      />
                    )
                  )}
                <TotalAllocateRow
                  dailyLogAllocations={
                    allocationLogDetails.allocationLogDetails
                      .totalDailyAllocationDetails
                  }
                  requestID={requestID}
                  selectedDateInDatePicker={dateObject}
                  canRetry={allocationLogDetails.allocationLogDetails.canRetry}
                  refetch={refetch}
                  status={allocationLogDetails.allocationLogDetails.status}
                />
                {(allocationLogDetails.allocationLogDetails.status ==
                  'InProgress' ||
                  allocationLogDetails.allocationLogDetails.status ==
                    'Retrying') && <InventorySkeleton refetch={refetch} />}
              </div>
            )}
          </div>
        )}
      </div>
      <AdotelSkeleton
        open={loadingForAllocationDetails}
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
    </>
  )
}

export default AllocationHistoryDetails
